import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useEffect, useRef, useState, type PropsWithChildren } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';

interface Props {
    chevronColor: string;
    className?: string;
}

export function ScrollableRow({ children, chevronColor, className }: PropsWithChildren<Props>) {
    const ref = useRef<HTMLElement | null>(null);
    const [ canScrollLeft, setCanScrollLeft ] = useState(false);
    const [ canScrollRight, setCanScrollRight ] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }

        const update = () => {
            const { scrollLeft, scrollWidth, clientWidth } = el;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
        };

        update();
        el.addEventListener('scroll', update);
        window.addEventListener('resize', update);
        return () => {
            el.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, []);

    const scrollLeft = () => ref.current?.scrollBy({ left: -150, behavior: 'smooth' });
    const scrollRight = () => ref.current?.scrollBy({ left: 150, behavior: 'smooth' });

    const leftMaskClasses = clsx(
        // left fade: transparent → opaque
        '[mask-image:linear-gradient(to_right,_transparent,_transparent_4rem,_black_5rem,_black_100%)]',
        '[mask-repeat:no-repeat]',
        'transition-[mask-position] duration-150',
        canScrollLeft
            ? '[mask-size:100%] [mask-position:0_0]' // fade visible
            : '[mask-size:200%] [mask-position:-5rem_0]' // move fade off-screen → solid black over visible area
    );

    const rightMaskClasses = clsx(
        // right fade: opaque → transparent
        '[mask-image:linear-gradient(to_right,_black_0,_black_calc(100%-5rem),_transparent_calc(100%-4rem),_transparent_100%)]',
        '[mask-repeat:no-repeat]',
        'transition-[mask-size] duration-150',
        canScrollRight
            ? '[mask-size:100%]' // fade visible
            : '[mask-size:200%]' // move fade off-screen
    );

    return (
        <div className={clsx('w-full relative', className)}>
            <div className={leftMaskClasses}>
                <div className={rightMaskClasses}>
                    <ScrollContainer innerRef={ref} className='w-full' hideScrollbars={true}>
                        {children}
                    </ScrollContainer>
                </div>
            </div>
            <div
                onClick={scrollLeft}
                className={`
                      absolute inset-y-0 left-0 w-12
                      flex items-start justify-start
                      transition-opacity duration-100
                      ${canScrollLeft ? 'opacity-100' : 'opacity-0'}
                    `}
            >
                <div className='h-36 mt-6 flex items-center'>
                    <ChevronLeftIcon className={clsx('w-15', chevronColor)} />
                </div>
            </div>

            <div
                onClick={scrollRight}
                className={`
                      absolute inset-y-0 right-0 w-12
                      flex items-start justify-end
                      transition-opacity duration-100
                      ${canScrollRight ? 'opacity-100' : 'opacity-0'}
                    `}
            >
                <div className='h-36 mt-6 flex items-center'>
                    <ChevronRightIcon className={clsx('w-15', chevronColor)} />
                </div>
            </div>
        </div>
    );
}
